import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, RotateCcw, Loader2 } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { keyCodeOf } from '@/lib/tagShortcuts';
import { tagThemeClass } from './tagTheme';
import { JewelleryMultiPicker } from './JewelleryMultiPicker';

// Opens Alt + D D D (Alt held, D pressed three times quickly) — super admin only.
export function useRestoreShortcut(enabled, onOpen) {
  useEffect(() => {
    if (!enabled) return undefined;
    let hits = []; // timestamps of Alt+D presses
    const onKey = (e) => {
      if (!e.altKey || keyCodeOf(e) !== 'KeyD') { if (e.key !== 'Alt') hits = []; return; }
      e.preventDefault();
      const now = Date.now();
      hits = [...hits.filter((t) => now - t < 1500), now];
      if (hits.length >= 3) { hits = []; onOpen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onOpen]);
}

export function TagRestoreDialog({ onClose, onRestored }) {
  const [deleted, setDeleted] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    notesApi.get('/notes/tag-workspace/deleted-tags').then(({ data }) => setDeleted(data || [])).catch((e) => { toast.error(extractError(e, 'Could not load deleted tags.')); setDeleted([]); });
  }, []);
  const restore = async () => {
    setBusy(true);
    try {
      const { data } = await notesApi.post('/notes/tag-workspace/restore-tags', { ids: picked.map((x) => x.id) });
      toast.success(`${data.restored} tag${data.restored === 1 ? '' : 's'} restored.`);
      const gone = new Set(picked.map((x) => x.id));
      setDeleted((d) => d.filter((x) => !gone.has(x.id)));
      setPicked([]); onRestored();
    } catch (e) { toast.error(extractError(e, 'Could not restore the tags.')); } finally { setBusy(false); }
  };
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[210] flex items-center justify-center md:p-3`} data-testid="tagws-restore-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full flex-col bg-background p-4 shadow-xl md:h-auto md:max-h-[90vh] md:max-w-xl md:rounded-2xl md:border md:border-border">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-base font-semibold text-foreground"><RotateCcw size={17} className="text-[hsl(var(--ring))]" /> Restore deleted tags <span className="font-mono text-xs font-normal text-muted-foreground" data-testid="tagws-restore-count">{deleted ? `${deleted.length} deleted` : ''}</span></h2>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground" data-testid="tagws-restore-close"><X size={18} /></button>
        </div>
        {deleted === null ? <Loader2 size={20} className="mx-auto my-8 animate-spin text-muted-foreground" /> : (
          <>
            <JewelleryMultiPicker items={deleted} excludeIds={picked.map((x) => x.id)} onAdd={(l) => setPicked((p) => [...p, ...l])} placeholder="Search deleted jewellery no." testid="tagws-restore-picker" note={(it) => (it.tagws_hidden_at ? `Deleted ${new Date(it.tagws_hidden_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : '')} />
            <div className="mt-3 min-h-[6rem] flex-1 space-y-1.5 overflow-y-auto" data-testid="tagws-restore-selected">
              {picked.length ? picked.map((x) => (
                <div key={x.id} className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-2.5 py-1.5" data-testid="tagws-restore-selected-item">
                  {x.image ? <img src={x.image} alt="" className="h-9 w-9 rounded-lg object-cover" /> : <span className="h-9 w-9 rounded-lg bg-secondary" />}
                  <span className="flex-1 truncate font-mono text-sm font-semibold">{x.jewellery_no}</span>
                  <span className="text-[length:calc(var(--fs,1)*0.625rem)] text-muted-foreground">{x.is_manual ? `Manual${x.owner ? ` · ${x.owner}` : ''}` : 'Main portal'}{x.tagws_hidden_at ? ` · deleted ${new Date(x.tagws_hidden_at).toLocaleDateString('en-GB')}` : ''}</span>
                  <button onClick={() => setPicked((p) => p.filter((y) => y.id !== x.id))} aria-label={`Remove ${x.jewellery_no}`} className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground hover:bg-secondary" data-testid="tagws-restore-selected-remove"><X size={13} /></button>
                </div>
              )) : <p className="py-6 text-center text-sm text-muted-foreground">{deleted.length ? 'Search and tick the pieces to restore.' : 'No deleted tags.'}</p>}
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={onClose} className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="tagws-restore-cancel">Close</button>
              <button onClick={restore} disabled={!picked.length || busy} className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-40" data-testid="tagws-restore-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Restore {picked.length || ''} selected</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
